<template>
  <v-dialog v-bind="$attrs" v-model="showDialog" persistent max-width="600px">
    <v-card>
      <v-card-title class="primaryDialogTitle">
        <span class="headline">Block Time Slot</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="6">
            <FletrixSelect
              v-model="selectedStartTime"
              :items="timeOptions"
              label="Start Time"
              :disabled="!timeOptions.length"
              prepend-inner-icon="mdi-clock-start"
            />
          </v-col>
          <v-col cols="6">
            <FletrixSelect
              v-model="selectedEndTime"
              :items="availableEndTimeOptions"
              label="End Time"
              :disabled="!selectedStartTime || !availableEndTimeOptions.length"
              prepend-inner-icon="mdi-clock-end"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <FletrixTextarea v-model="remarks" label="Remarks" />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :disabled="!isValidTimeRange" @click="markRest"> Save </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import { crudCreate } from '@/api/crud';
import { IDateRange, populatedCtor } from '@/api/mongooseTypes';
import { ITimetable } from '@/api/timetable';
import { getOid } from '@/services/utils';
import { parseAndLocalize } from '@/services/filters';
import moment from 'moment';
import { FletrixSelect, FletrixTextarea } from '@/components/Atom';

export default {
  name: 'TakeRestDialog',
  components: { FletrixSelect, FletrixTextarea },
  props: {
    value: Boolean,
    time: IDateRange,
    clinic: Object,
    date: Date,
  },
  data() {
    return {
      selectedStartTime: null,
      selectedEndTime: null,
      remarks: '',
      timeOptions: [],
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    isValidTimeRange() {
      return (
        this.selectedStartTime &&
        this.selectedEndTime &&
        this.selectedStartTime < this.selectedEndTime
      );
    },
    availableEndTimeOptions() {
      if (!this.selectedStartTime) return [];

      const startIndex = this.timeOptions.findIndex(
        option => option.value === this.selectedStartTime,
      );
      if (startIndex === -1) return [];

      // Return all time options after the selected start time
      return this.timeOptions.slice(startIndex + 1);
    },
    startDateTime() {
      if (!this.selectedStartTime || !this.date) return null;
      return moment(this.date).startOf('day').add(this.selectedStartTime, 'minutes').toDate();
    },
    endDateTime() {
      if (!this.selectedEndTime || !this.date) return null;
      return moment(this.date).startOf('day').add(this.selectedEndTime, 'minutes').toDate();
    },
  },
  watch: {
    showDialog(newValue) {
      if (newValue) {
        this.resetForm();
      }
    },
    selectedStartTime() {
      // Reset end time when start time changes
      if (this.selectedEndTime && this.selectedStartTime >= this.selectedEndTime) {
        this.selectedEndTime = null;
      }
    },
  },
  methods: {
    parseAndLocalize,
    close() {
      this.showDialog = false;
      this.$emit('close');
    },
    generateTimeOptions() {
      const options = [];
      // Generate time options from 00:00 to 23:45 with 15-minute intervals
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const timeString = moment({ hour, minute }).format('HH:mm');
          const totalMinutes = hour * 60 + minute;
          options.push({
            text: timeString,
            value: totalMinutes,
          });
        }
      }
      return options;
    },
    resetForm() {
      // Generate time options
      this.timeOptions = this.generateTimeOptions();

      // Initialize start and end times based on the provided time prop or current time
      if (this.time && this.time.start) {
        const startMoment = moment(this.time.start);
        const startMinutes = startMoment.hours() * 60 + startMoment.minutes();
        // Round to nearest 15-minute interval
        const roundedStartMinutes = Math.round(startMinutes / 15) * 15;
        this.selectedStartTime = roundedStartMinutes;

        // Default end time to 15 minutes after start time
        const endMinutes = roundedStartMinutes + 15;
        this.selectedEndTime = endMinutes < 24 * 60 ? endMinutes : roundedStartMinutes + 15;
      } else if (this.date) {
        // If no specific time provided, use current time or a default time on the given date
        const now = moment();
        const currentMinutes = now.hours() * 60 + now.minutes();
        // Round to next 15-minute interval
        const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;
        this.selectedStartTime = roundedMinutes < 24 * 60 ? roundedMinutes : 0;

        // Default end time to 15 minutes after start time
        const endMinutes = this.selectedStartTime + 15;
        this.selectedEndTime = endMinutes < 24 * 60 ? endMinutes : this.selectedStartTime + 15;
      } else {
        // Fallback to current time
        const now = moment();
        const currentMinutes = now.hours() * 60 + now.minutes();
        const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;
        this.selectedStartTime = roundedMinutes < 24 * 60 ? roundedMinutes : 0;
        this.selectedEndTime = this.selectedStartTime + 15;
      }

      this.remarks = '';
    },
    async markRest() {
      if (!this.isValidTimeRange) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Please select a valid time range',
          type: 'error',
        });
        return;
      }

      try {
        const model = {
          clinic: getOid(this.clinic),
          time: {
            start: this.startDateTime,
            end: this.endDateTime,
          },
          remarks: this.remarks || undefined, // Only include remarks if provided
        };

        const out = populatedCtor(
          await crudCreate(api, 'rml/admin/timetables/takeRest', model),
          ITimetable,
        );

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Time slot blocked successfully',
          type: 'success',
          color: 'success',
        });

        this.$emit('create', out);
        this.close();
      } catch (err) {
        console.error('Error blocking time slot:', err);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to block time slot',
          type: 'error',
        });
      }
    },
  },
};
</script>

<style scoped></style>
